import type { ContextFeatureAllowedValue, ContextFeatureJsonValue, TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import type { TradeLogicContextFeatureRef, TradeLogicFeatureFilter, TradeLogicTrailingMode } from '../model';
import type {
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryConditionTemplateFeatureOptionDto,
    TradeLogicRegistryFeatureDto,
    TradeLogicRegistrySchemaFieldDto
} from '../registry';

const ALLOWED_VALUE_PREFIX = 'allowed:';
export const RANGE_OPERATORS = new Set(['between', 'outside']);
export const VALUELESS_OPERATORS = new Set(['empty', 'not_empty']);
const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in', 'any', 'all']);
const SCALAR_MEMBERSHIP_OPERATORS = new Set(['contains', 'not_contains']);

export const schemaValueToInputString = (value: ContextFeatureJsonValue | undefined | null): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);

    return JSON.stringify(value);
};

export const schemaAllowedValueToInputString = (value: ContextFeatureAllowedValue): string => {
    return `${ALLOWED_VALUE_PREFIX}${JSON.stringify({ type: typeof value, value })}`;
};

export const parseSchemaInputValue = (rawValue: string, field: TradeLogicRegistrySchemaFieldDto): ContextFeatureJsonValue => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') {
        if (field.required) {
            throw new Error(`${field.label} is required`);
        }

        return null;
    }

    if (field.controlType === 'multiSelect') {
        const parts = rawValue
            .split(',')
            .map((part) => part.trim())
            .filter((part) => part.length > 0);

        if (parts.length === 0) {
            if (field.required) {
                throw new Error(`${field.label} is required`);
            }

            return null;
        }

        if (field.allowedValues.length > 0) {
            const matched: ContextFeatureAllowedValue[] = [];

            for (const part of parts) {
                const found = parseAllowedValueInput(part, field.allowedValues);

                if (found === undefined) {
                    throw new Error(`${field.label} contains an invalid value: ${part}`);
                }

                matched.push(found);
            }

            return matched;
        }

        return parts.map((part) => parseSchemaScalarValue(part, field));
    }

    if (field.allowedValues.length > 0) {
        const selectedValue = parseAllowedValueInput(rawValue, field.allowedValues);

        if (selectedValue !== undefined) {
            return selectedValue;
        }

        throw new Error(`${field.label} must be one of the allowed values`);
    }

    return parseSchemaScalarValue(rawValue, field);
};

export const buildDefaultSchemaValues = (fields: TradeLogicRegistrySchemaFieldDto[]): Record<string, string> => {
    return Object.fromEntries(fields.map((field) => [field.key, schemaFieldValueToInputString(field, field.defaultValue)]));
};

export const buildParsedSchemaValues = (
    fields: TradeLogicRegistrySchemaFieldDto[],
    rawValues: Record<string, string>
): Record<string, ContextFeatureJsonValue> => {
    return Object.fromEntries(
        fields
            .filter((field) => field.required || (rawValues[field.key] ?? '').trim() !== '')
            .map((field) => [field.key, parseSchemaInputValue(rawValues[field.key] ?? '', field)])
    );
};

export const buildSchemaInputValues = (
    fields: TradeLogicRegistrySchemaFieldDto[],
    values: Record<string, ContextFeatureJsonValue> | null | undefined
): Record<string, string> => {
    return Object.fromEntries(fields.map((field) => [field.key, schemaFieldValueToInputString(field, values?.[field.key] ?? null)]));
};

export const buildFeatureValueField = (
    feature: TradeLogicRegistryFeatureDto,
    operator?: string | null
): TradeLogicRegistrySchemaFieldDto => {
    const baseControlType = feature.valueControl?.controlType ?? fallbackControlType(feature);
    const shouldUseMultiValueControl = operator !== null && operator !== undefined && MULTI_VALUE_OPERATORS.has(operator);
    const shouldUseScalarMembershipControl =
        operator !== null &&
        operator !== undefined &&
        SCALAR_MEMBERSHIP_OPERATORS.has(operator) &&
        (feature.valueShape === 'enumArray' || feature.valueShape === 'stringArray');
    const controlType = shouldUseScalarMembershipControl
        ? scalarMembershipControlType(feature)
        : shouldUseMultiValueControl
          ? multiValueControlType(baseControlType)
          : baseControlType;

    return {
        key: 'value',
        type: schemaTypeForFeatureValue(feature),
        operators: feature.allowedOperators,
        required: false,
        label: feature.valueControl?.label ?? 'Value',
        description: feature.valueControl?.description ?? null,
        allowedValues: feature.valueControl?.allowedValues ?? feature.valueEnum,
        defaultValue: feature.valueControl?.defaultValue ?? null,
        examples: feature.valueControl?.examples ?? [],
        controlType,
        min: feature.valueControl?.min ?? null,
        max: feature.valueControl?.max ?? null,
        step: feature.valueControl?.step ?? null,
        placeholder: feature.valueControl?.placeholder ?? null
    };
};

export const parseConditionValue = (feature: TradeLogicRegistryFeatureDto, operator: string, rawValue: string): ContextFeatureJsonValue => {
    if (VALUELESS_OPERATORS.has(operator)) {
        return null;
    }

    if (rawValue.trim() === '') return null;

    if (RANGE_OPERATORS.has(operator)) {
        const [leftValue, rightValue, ...extraValues] = rawValue.split(',').map((value) => value.trim());

        if (!leftValue || !rightValue || extraValues.length > 0) {
            throw new Error('Range operators require two comma-separated values');
        }

        const valueField = buildFeatureValueField(feature);

        return [parseSchemaInputValue(leftValue, valueField), parseSchemaInputValue(rightValue, valueField)];
    }

    return parseSchemaInputValue(rawValue, buildFeatureValueField(feature, operator));
};

export const conditionValueToInputString = (
    feature: TradeLogicRegistryFeatureDto,
    operator: string,
    value: ContextFeatureJsonValue | undefined
): string => {
    if (VALUELESS_OPERATORS.has(operator)) return '';
    if (value === undefined || value === null) return '';

    if (RANGE_OPERATORS.has(operator) && Array.isArray(value)) {
        const valueField = buildFeatureValueField(feature);
        return value.map((item) => schemaFieldValueToInputString(valueField, item)).join(', ');
    }

    return schemaFieldValueToInputString(buildFeatureValueField(feature, operator), value);
};

export const buildFeatureFilters = (
    fields: TradeLogicRegistrySchemaFieldDto[],
    rawValues: Record<string, string>,
    operators: Record<string, TradeLogicComparisonOperatorV2>
): TradeLogicFeatureFilter[] => {
    const parsedValues = buildParsedSchemaValues(fields, rawValues);

    return Object.entries(parsedValues).map(([fieldKey, value]) => {
        const field = fields.find((candidateField) => candidateField.key === fieldKey);

        return {
            field: fieldKey,
            operator: operators[fieldKey] ?? field?.operators[0] ?? '=',
            value
        };
    });
};

export const buildFilterInputState = (
    fields: TradeLogicRegistrySchemaFieldDto[],
    filters: TradeLogicFeatureFilter[] | null | undefined
): {
    operators: Record<string, TradeLogicComparisonOperatorV2>;
    values: Record<string, string>;
} => {
    const filtersByField = new Map(filters?.map((filter) => [filter.field, filter]) ?? []);

    return {
        operators: Object.fromEntries(
            fields.map((field) => [field.key, filtersByField.get(field.key)?.operator ?? field.operators[0] ?? '='])
        ),
        values: Object.fromEntries(
            fields.map((field) => [field.key, schemaFieldValueToInputString(field, filtersByField.get(field.key)?.value ?? null)])
        )
    };
};

export interface TrailingInputState {
    trailingMode: '' | TradeLogicTrailingMode;
    withinBars: string;
}

export const buildTrailingInputState = (featureRef: TradeLogicContextFeatureRef | null | undefined): TrailingInputState => ({
    trailingMode: featureRef?.trailingMode ?? '',
    withinBars: featureRef?.withinBars === null || featureRef?.withinBars === undefined ? '' : String(featureRef.withinBars)
});

export const parseTrailingInputState = (
    state: TrailingInputState
): { trailingMode: TradeLogicTrailingMode | null; withinBars: number | null } => {
    const trailingMode = state.trailingMode || null;
    const rawWithinBars = state.withinBars.trim();

    if (rawWithinBars === '') {
        return { trailingMode, withinBars: null };
    }

    const withinBars = Number(rawWithinBars);

    if (!Number.isInteger(withinBars) || withinBars <= 0) {
        throw new Error('Within Bars must be a positive integer');
    }

    if (!trailingMode) {
        throw new Error('Within Bars requires a trailing mode');
    }

    return { trailingMode, withinBars };
};

export const getConditionTemplateFeatureRef = (
    featureOption: TradeLogicRegistryConditionTemplateFeatureOptionDto | null
): TradeLogicContextFeatureRef | null => {
    const featureRef = featureOption?.featureRef ?? null;
    const featureKey = featureRef?.featureKey ?? featureOption?.featureKey ?? null;

    if (!featureKey) return null;

    return {
        kind: 'contextFeature',
        featureKey,
        duration: featureRef?.duration ?? null,
        bindingKey: featureRef?.bindingKey ?? null,
        entityType: featureRef?.entityType ?? null,
        entityKey: featureRef?.entityKey ?? null,
        selectors: featureRef?.selectors ?? null,
        filters:
            featureRef?.filters?.map((filter) => ({
                field: filter.field,
                operator: filter.operator,
                value: filter.value ?? null
            })) ?? null,
        offsetBars: featureRef?.offsetBars ?? null,
        lookbackBars: featureRef?.lookbackBars ?? null,
        trailingMode: featureRef?.trailingMode ?? null,
        withinBars: featureRef?.withinBars ?? null,
        missingDataPolicy: featureRef?.missingDataPolicy ?? null
    };
};

export const buildConditionTemplateValueField = (
    template: TradeLogicRegistryConditionTemplateDto | null
): TradeLogicRegistrySchemaFieldDto | null => {
    if (!template?.valueSchema) return null;

    return {
        key: 'value',
        type: template.valueSchema.type,
        operators: template.allowedOperators,
        required: template.valueSchema.required,
        label: template.valueSchema.label ?? 'Value',
        description: template.valueSchema.description ?? null,
        allowedValues: template.valueSchema.allowedValues ?? [],
        defaultValue: template.valueSchema.defaultValue ?? null,
        examples: template.valueSchema.examples ?? [],
        controlType: null,
        min: null,
        max: null,
        step: null,
        placeholder: null
    };
};

export const formatGroupPathLabel = (groupPath: string[], fallback: string): string => {
    const dedupedPath: string[] = [];

    for (const segment of groupPath) {
        if (segment.length === 0) continue;
        if (dedupedPath[dedupedPath.length - 1] === segment) continue;
        dedupedPath.push(segment);
    }

    if (dedupedPath.length === 0) return fallback;
    return dedupedPath.join(' › ');
};

// Optimizer discoverability curates the default picker; it does not determine
// whether runtime-only features (for example market clocks) can be authored.
export const filterAuthoringFeatures = (features: TradeLogicRegistryFeatureDto[], showAll: boolean): TradeLogicRegistryFeatureDto[] =>
    features.filter(
        (feature) =>
            feature.capabilities.canAuthor && (showAll || (feature.isDiscoverable && (feature.tier === 'primary' || feature.tier === null)))
    );

export const groupRegistryFeatures = (
    features: TradeLogicRegistryFeatureDto[],
    searchText: string
): { label: string; features: TradeLogicRegistryFeatureDto[] }[] => {
    const query = searchText.trim().toLowerCase();
    const groups = new Map<string, TradeLogicRegistryFeatureDto[]>();

    for (const feature of features) {
        if (query && !feature.searchText.includes(query)) continue;

        const groupKey = formatGroupPathLabel(feature.groupPath, feature.groupLabel);
        const existing = groups.get(groupKey) ?? [];
        existing.push(feature);
        groups.set(groupKey, existing);
    }

    return Array.from(groups.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([label, groupedFeatures]) => ({
            label,
            features: groupedFeatures.sort((left, right) => left.displayLabel.localeCompare(right.displayLabel))
        }));
};

export const schemaFieldValueToInputString = (
    field: TradeLogicRegistrySchemaFieldDto,
    value: ContextFeatureJsonValue | undefined | null
): string => {
    if (value === undefined || value === null) return '';

    if (field.controlType === 'multiSelect' && Array.isArray(value)) {
        return value.map((item) => (isAllowedPrimitiveValue(item) ? String(item) : JSON.stringify(item))).join(', ');
    }

    if (isAllowedPrimitiveValue(value) && field.allowedValues.some((allowedValue) => allowedValue === value)) {
        return schemaAllowedValueToInputString(value);
    }

    return schemaValueToInputString(value);
};

export const parseAllowedValueInput = (
    rawValue: string,
    allowedValues: ContextFeatureAllowedValue[]
): ContextFeatureAllowedValue | undefined => {
    if (rawValue.startsWith(ALLOWED_VALUE_PREFIX)) {
        try {
            const decoded = JSON.parse(rawValue.slice(ALLOWED_VALUE_PREFIX.length)) as {
                type?: string;
                value?: ContextFeatureAllowedValue;
            };

            return allowedValues.find((allowedValue) => typeof allowedValue === decoded.type && allowedValue === decoded.value);
        } catch {
            return undefined;
        }
    }

    return allowedValues.find((allowedValue) => String(allowedValue) === rawValue);
};

const isAllowedPrimitiveValue = (value: ContextFeatureJsonValue): value is ContextFeatureAllowedValue => {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
};

const parseSchemaScalarValue = (rawValue: string, field: TradeLogicRegistrySchemaFieldDto): ContextFeatureJsonValue => {
    switch (field.type) {
        case 'boolean':
            if (rawValue !== 'true' && rawValue !== 'false') {
                throw new Error(`${field.label} must be true or false`);
            }

            return rawValue === 'true';

        case 'integer': {
            const parsed = Number(rawValue);

            if (!Number.isInteger(parsed)) {
                throw new Error(`${field.label} must be an integer`);
            }

            return parsed;
        }

        case 'number': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) {
                throw new Error(`${field.label} must be numeric`);
            }

            return parsed;
        }

        case 'timestamp': {
            const parsed = Number(rawValue);

            if (!Number.isFinite(parsed)) {
                throw new Error(`${field.label} must be numeric`);
            }

            return parsed;
        }

        case 'json': {
            try {
                return JSON.parse(rawValue) as ContextFeatureJsonValue;
            } catch {
                throw new Error(`${field.label} must be valid JSON`);
            }
        }

        case 'bindingKey':
        case 'duration':
        case 'enum':
        case 'levelKey':
        case 'string':
        case null:
        default:
            return rawValue;
    }
};

const schemaTypeForFeatureValue = (feature: TradeLogicRegistryFeatureDto): TradeLogicRegistrySchemaFieldDto['type'] => {
    switch (feature.valueShape) {
        case null:
            return 'string';
        case 'boolean':
            return 'boolean';
        case 'duration':
            return 'duration';
        case 'enum':
        case 'enumArray':
            return 'enum';
        case 'number':
        case 'numberArray':
        case 'price':
            return feature.valueKind === 'integer' ? 'integer' : 'number';
        case 'timestamp':
            return 'timestamp';
        case 'string':
        case 'stringArray':
            return 'string';
        case 'json':
        case 'object':
        case 'objectArray':
            return 'json';
        default:
            return 'string';
    }
};

const fallbackControlType = (feature: TradeLogicRegistryFeatureDto): TradeLogicRegistrySchemaFieldDto['controlType'] => {
    switch (feature.valueShape) {
        case null:
            return 'text';
        case 'boolean':
            return 'boolean';
        case 'duration':
            return 'duration';
        case 'enum':
            return 'select';
        case 'enumArray':
        case 'stringArray':
            return 'multiSelect';
        case 'number':
        case 'numberArray':
        case 'price':
        case 'timestamp':
            return 'number';
        case 'string':
            return 'text';
        case 'json':
        case 'object':
        case 'objectArray':
            return 'json';
        default:
            return 'text';
    }
};

const scalarMembershipControlType = (feature: TradeLogicRegistryFeatureDto): TradeLogicRegistrySchemaFieldDto['controlType'] => {
    return feature.valueShape === 'enumArray' ? 'select' : 'text';
};

const multiValueControlType = (
    controlType: TradeLogicRegistrySchemaFieldDto['controlType']
): TradeLogicRegistrySchemaFieldDto['controlType'] => {
    if (controlType === 'json') return 'json';
    return 'multiSelect';
};
